import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Alert,
} from '@mui/material';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import PaymentsIcon from '@mui/icons-material/Payments';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { approvalService } from '../../services/workspaceService';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  TypePill,
  ErrorState,
  TableEmptyState,
  StatusTone,
} from '../../components/common/pageKit';
import { formatIndianNumber, formatCurrency } from '../../utils/currency';

/**
 * Approvals Center — the CRM slice of the generic erp-api approval workflow
 * (approval_requests / approval_actions, see src/controllers/approvals.js).
 * Scoped here to entity_type IN ('opportunity', 'discount'); other modules
 * (finance, procurement, ...) get their own view onto the same backend.
 */

// ---------------------------------------------------------------------------
// Types — mirror the `approval_requests` / `approval_actions` rows. The
// backend does a plain `SELECT *` with no user join, so requester/approver
// names are best-effort and fall back to a user-id label below.
// ---------------------------------------------------------------------------

type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

interface ApprovalRequestRow {
  id: number;
  entity_type: string;
  entity_id: number;
  title: string;
  amount: number | string | null;
  status: ApprovalStatus;
  current_step: number;
  requested_by: number;
  requested_by_name?: string | null;
  requester_name?: string | null;
  completed_at?: string | null;
  created_at: string;
}

interface ApprovalAction {
  id: number;
  step_number: number;
  approver_id: number;
  approver_name?: string | null;
  action: 'APPROVED' | 'REJECTED' | 'DELEGATED' | 'COMMENTED';
  comments?: string | null;
  created_at: string;
}

interface ApprovalDetail {
  request: ApprovalRequestRow;
  actions: ApprovalAction[];
}

const CRM_ENTITY_TYPES = ['opportunity', 'discount'] as const;

const TYPE_TINT: Record<string, string> = {
  opportunity: '#0284c7',
  discount: '#7c3aed',
};

const typeLabel = (entityType: string): string =>
  entityType === 'opportunity' ? 'OPPORTUNITY' : entityType === 'discount' ? 'DISCOUNT' : entityType.toUpperCase();

const STATUS_TONE: Record<ApprovalStatus, StatusTone> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  CANCELLED: 'neutral',
};

const fmtDate = (v?: string | null): string => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (v?: string | null): string => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const requesterLabel = (row: ApprovalRequestRow): string =>
  row.requested_by_name || row.requester_name || (row.requested_by ? `User #${row.requested_by}` : '—');

const isThisWeek = (v?: string | null): boolean => {
  if (!v) return false;
  const t = new Date(v).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= 7 * 24 * 60 * 60 * 1000;
};

/** nowrap + ellipsis + title + compact, per the money-tile clipping rule. */
const MoneyText: React.FC<{ value: number | string | null | undefined }> = ({ value }) => {
  const n = value === null || value === undefined || value === '' ? null : Number(value);
  if (n === null || Number.isNaN(n)) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Typography
      variant="body2"
      title={formatCurrency(n)}
      sx={{
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 140,
        display: 'block',
      }}
    >
      {formatIndianNumber(n)}
    </Typography>
  );
};

const ApprovalsCenterPage: React.FC = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState<ApprovalRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'PENDING' | 'ALL'>('PENDING');

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ApprovalDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [acting, setActing] = useState<'APPROVE' | 'REJECT' | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all(CRM_ENTITY_TYPES.map((entityType) => approvalService.requests({ entityType })))
      .then((results: any[]) => {
        const merged: ApprovalRequestRow[] = results.flatMap(
          (r) => (r?.requests as ApprovalRequestRow[]) || []
        );
        merged.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        setRows(merged);
      })
      .catch((e: any) => setError(e?.message || 'Failed to load approvals.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pendingRows = useMemo(() => rows.filter((r) => r.status === 'PENDING'), [rows]);
  const pendingValue = useMemo(
    () =>
      pendingRows.reduce(
        (sum, r) => sum + (r.amount === null || r.amount === undefined ? 0 : Number(r.amount) || 0),
        0
      ),
    [pendingRows]
  );
  const approvedThisWeek = useMemo(
    () => rows.filter((r) => r.status === 'APPROVED' && isThisWeek(r.completed_at)).length,
    [rows]
  );

  const visibleRows = statusFilter === 'PENDING' ? pendingRows : rows;

  const openDetail = (id: number) => {
    setSelectedId(id);
    setComment('');
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    approvalService
      .detail(id)
      .then((res: any) => setDetail(res))
      .catch((e: any) => setDetailError(e?.message || 'Failed to load this request.'))
      .finally(() => setDetailLoading(false));
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
    setDetailError(null);
    setComment('');
    setActing(null);
  };

  const act = (action: 'APPROVE' | 'REJECT') => {
    if (selectedId === null) return;
    setActing(action);
    setDetailError(null);
    const call = action === 'APPROVE' ? approvalService.approve : approvalService.reject;
    call(selectedId, comment.trim() || undefined)
      .then(() => {
        closeDetail();
        load();
      })
      .catch((e: any) => {
        setDetailError(e?.message || `Failed to ${action.toLowerCase()} this request.`);
        setActing(null);
      });
  };

  const goToOpportunity = (entityId: number) => navigate(`/crm/opportunity/${entityId}/workspace`);

  const detailRequest = detail?.request;
  const canAct = detailRequest?.status === 'PENDING';

  return (
    <Box>
      <PageHeader
        title="Approvals Center"
        subtitle="Opportunity and discount approvals awaiting a decision"
        actions={
          <IconButton onClick={load} disabled={loading} aria-label="Refresh approvals">
            <RefreshIcon />
          </IconButton>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Pending Approvals"
            value={loading ? null : pendingRows.length}
            icon={<HourglassTopIcon />}
            color="#f59e0b"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Pending Value"
            value={loading ? null : formatIndianNumber(pendingValue)}
            icon={<PaymentsIcon />}
            color="#0284c7"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Approved This Week"
            value={loading ? null : approvedThisWeek}
            icon={<TaskAltIcon />}
            color="#16a34a"
          />
        </Grid>
      </Grid>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }} flexWrap="wrap" gap={1}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, v) => v && setStatusFilter(v)}
        >
          <ToggleButton value="PENDING">Pending</ToggleButton>
          <ToggleButton value="ALL">All</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <TableSurface>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <ErrorState message={error} onRetry={load} />
        ) : visibleRows.length === 0 ? (
          <TableEmptyState
            title="No approvals awaiting you"
            hint={statusFilter === 'PENDING' ? 'Switch to "All" to see approved, rejected or cancelled requests.' : undefined}
          />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Title</TableCell>
                <TableCell sx={headerCellSx}>Type</TableCell>
                <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                <TableCell sx={headerCellSx}>Requested By</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx}>Created</TableCell>
                <TableCell sx={headerCellSx} align="right">Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ ...bodyRowSx, cursor: 'pointer' }}
                  onClick={() => openDetail(row.id)}
                  hover
                >
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={row.title}
                    >
                      {row.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <TypePill label={typeLabel(row.entity_type)} tint={TYPE_TINT[row.entity_type] || '#475569'} />
                  </TableCell>
                  <TableCell align="right">
                    <MoneyText value={row.amount} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{requesterLabel(row)}</Typography>
                  </TableCell>
                  <TableCell>
                    <StatusDot label={row.status} tone={STATUS_TONE[row.status] || 'neutral'} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {fmtDate(row.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {row.entity_type === 'opportunity' && (
                      <Tooltip title="Open opportunity workspace">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            goToOpportunity(row.entity_id);
                          }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableSurface>

      <Dialog open={selectedId !== null} onClose={closeDetail} maxWidth="sm" fullWidth>
        <DialogTitle>Approval Request</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={26} />
            </Box>
          ) : detailError && !detailRequest ? (
            <ErrorState message={detailError} onRetry={selectedId !== null ? () => openDetail(selectedId) : undefined} />
          ) : detailRequest ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {detailRequest.title}
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
                  <TypePill
                    label={typeLabel(detailRequest.entity_type)}
                    tint={TYPE_TINT[detailRequest.entity_type] || '#475569'}
                  />
                  <StatusDot label={detailRequest.status} tone={STATUS_TONE[detailRequest.status] || 'neutral'} />
                </Stack>
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Amount</Typography>
                  <Box><MoneyText value={detailRequest.amount} /></Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Requested By</Typography>
                  <Typography variant="body2">{requesterLabel(detailRequest)}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Current Step</Typography>
                  <Typography variant="body2">{detailRequest.current_step}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Created</Typography>
                  <Typography variant="body2">{fmtDateTime(detailRequest.created_at)}</Typography>
                </Grid>
              </Grid>

              {detailRequest.entity_type === 'opportunity' && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<OpenInNewIcon fontSize="small" />}
                  onClick={() => goToOpportunity(detailRequest.entity_id)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Open opportunity workspace
                </Button>
              )}

              {(detail?.actions?.length || 0) > 0 && (
                <Box>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary' }}>
                    History
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {detail!.actions.map((a) => (
                      <Box key={a.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {a.action} — {a.approver_name || `User #${a.approver_id}`}
                          </Typography>
                          {a.comments && (
                            <Typography variant="caption" color="text.secondary">
                              {a.comments}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {fmtDateTime(a.created_at)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {canAct && (
                <TextField
                  label="Comments (optional)"
                  multiline
                  minRows={2}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  fullWidth
                />
              )}

              {detailError && <Alert severity="error">{detailError}</Alert>}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Close</Button>
          {canAct && (
            <>
              <Button
                color="error"
                variant="outlined"
                disabled={acting !== null}
                onClick={() => act('REJECT')}
              >
                {acting === 'REJECT' ? <CircularProgress size={18} /> : 'Reject'}
              </Button>
              <Button
                color="success"
                variant="contained"
                disabled={acting !== null}
                onClick={() => act('APPROVE')}
              >
                {acting === 'APPROVE' ? <CircularProgress size={18} color="inherit" /> : 'Approve'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApprovalsCenterPage;
